import {put, call} from 'redux-saga/effects';
import FlatFee from "../../entities/flatFee";
import * as Actions from "../actions/flatFeeActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type FlatFeeAction = {
    flatFeeId: string;
    flatFee: FlatFee;
    flatFees: FlatFee[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneFlatFee(action: FlatFeeAction) {
	const session = tabSession.captureGeneration();
	try {
		const flatFee: FlatFee = yield call(api.findOne, 'flatFee', action.flatFeeId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.FLATFEE_FETCH_ONE_SUCCESS, flatFeeId: action.flatFeeId, flatFee});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FLATFEE_FETCH_ONE_FAILURE, flatFeeId: action.flatFeeId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {FlatFeeAction} action
 */
export function* saveFlatFee(action: FlatFeeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ FLATFEE_SAVE saga', action.flatFee?.id);
    
	if (action.flatFee === undefined || action.flatFee === null) {
		console.error('saveFlatFee received a null');
		throw new Error('saveFlatFee received a null');
	}

	if (!(action.flatFee instanceof FlatFee)) {
		action.flatFee = new FlatFee(action.flatFee);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'flatFee', action.flatFee);
		if (addResult.isErr()) {
			console.error(`⮂ saveFlatFee: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.FLATFEE_SAVE_FAILURE, flatFee: action.flatFee, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.FLATFEE_SAVE, flatFee: action.flatFee});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.FLATFEE_SAVE_FAILURE, flatFee: action.flatFee, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {FlatFeeAction} action
 * @param {FlatFee[]} action.flatFees
 */
export function* saveManyFlatFees(action: FlatFeeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ FLATFEE_SAVE_MANY saga', action.flatFees.map(flatFee => flatFee.id).join(','));
    
    if (!Array.isArray(action.flatFees)) {
        console.error('saveManyFlatFees expected an array, received', action.flatFees);
        throw new Error('saveManyFlatFees expected an array');
    }

	try {
		const toSave: FlatFee[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.flatFees.length; i++) {
			const flatFee = action.flatFees[i];
			flatFee.persisted = false;
			toSave.push(flatFee);
		}

		const addResult = yield call(idb.add, 'flatFee', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyFlatFees: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.FLATFEE_SAVE_MANY_FAILURE, flatFees: action.flatFees, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.FLATFEE_SAVE_MANY, flatFees: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.FLATFEE_SAVE_MANY_FAILURE, flatFees: action.flatFees, status: 500, err});

		console.error(`Error saving ${action.flatFees.length} flatFees to database`, err);
	}
}

/**
 * @param {FlatFeeAction} action
 */
export function* removeFlatFee(action: FlatFeeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.flatFee === undefined || action.flatFee === null || !(action.flatFee instanceof FlatFee)) {
			console.error('removeFlatFee expected type FlatFee, received', action.flatFee);
			throw new Error('removeFlatFee expected type FlatFee');
		}

	try {
		// Mark as deleted
		action.flatFee.status = 'D';
		action.flatFee.addedOn = newHRTimestamp();
		action.flatFee.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'flatFee', action.flatFee);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeFlatFee: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.FLATFEE_REMOVE_FAILURE, flatFee: action.flatFee, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.FLATFEE_REMOVE, flatFee: action.flatFee});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FLATFEE_REMOVE_FAILURE, flatFee: action.flatFee, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this FlatFee.
	*
	* @param {FlatFeeAction} action
	*/
export function* restoreFlatFee(action: FlatFeeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreFlatFee expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.flatFee === undefined || action.flatFee === null) {
        console.error('restoreFlatFee expected a FlatFee, received null');
        throw new Error('restoreFlatFee expected a FlatFee, received null');
    }
    else if (!(action.flatFee instanceof FlatFee)) action.flatFee = new FlatFee(action.flatFee); 
    
	try {
		// Mark as restored
		action.flatFee.status = 'U';
		action.flatFee.addedOn = newHRTimestamp();
		action.flatFee.addedBy = userId;
		action.flatFee.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'flatFee', action.flatFee);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreFlatFee: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.FLATFEE_RESTORE_FAILURE, flatFee: action.flatFee, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.FLATFEE_RESTORE, flatFee: action.flatFee});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FLATFEE_RESTORE_FAILURE, flatFee: action.flatFee, status: 500, err});

		console.error('Error restoring from database', err);
	}
}